import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea';
import { cn } from '@/lib/utils';
import type {
  DesignChange,
  UpdateDesignChangeReviewRequest,
} from '../../../../types/drbfm';
import { CARD_INPUT, CARD_TEXTAREA, REJECT_BUTTON, SUCCESS_OUTLINE_BADGE } from './styles';
import { useComparabilityConfirmation, useDesignChangeReviewDraft } from './useDesignChangeReviewDraft';

interface DesignChangeCardProps {
  change: DesignChange;
  index: number;
  statusLabel: string;
  isGenerationTarget: boolean;
  isLoading: boolean;
  onReviewChange: (
    designChangeId: string,
    request: UpdateDesignChangeReviewRequest,
  ) => void | boolean | Promise<void | boolean>;
  onConfirmComparability?: (designChangeId: string, rationale: string) => void | Promise<void>;
}

// A decision button must not take focus: the field's blur would save first and the button,
// disabled while that save runs, would drop the decision (#1525).
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

const DesignChangeCard: React.FC<DesignChangeCardProps> = ({
  change,
  index,
  statusLabel,
  isGenerationTarget,
  isLoading,
  onReviewChange,
  onConfirmComparability,
}) => {
  const { t } = useTranslation();
  const {
    draft, edit, saveEdit, decide, incomplete, issues, confirmation, held,
  } = useDesignChangeReviewDraft(change, onReviewChange);
  const confirmForm = useComparabilityConfirmation(
    change.id, confirmation.confirmed, onConfirmComparability,
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="revy-cjk text-base leading-[1.75]">
          {change.itemName
            || t('drbfm.workflow.cards.changeItem', '変更点 {{number}}', { number: index + 1 })}
        </h3>
        <Badge variant="secondary">{change.category}</Badge>
        <Badge
          variant="outline"
          className={cn('revy-cjk', isGenerationTarget && SUCCESS_OUTLINE_BADGE)}
        >
          {statusLabel}
        </Badge>
        {change.confidence != null && (
          <Badge variant="outline" className="revy-cjk revy-figure">
            {t('drbfm.workflow.cards.confidence', '信頼度 {{percent}}%', {
              percent: Math.round(change.confidence * 100),
            })}
          </Badge>
        )}
      </div>

      {issues.length > 0 && (
        <div role="note" className="revy-cjk text-sm text-muted-foreground">
          <p className="font-medium text-foreground">
            {confirmation.confirmed
              ? t('drbfm.workflow.cards.comparabilityConfirmed', '比較可能性確認済み: {{rationale}}', {
                rationale: confirmation.rationale,
              })
              : t('drbfm.workflow.cards.integrityHeld', '抽出の整合性確認のため保留中')}
          </p>
          <ul className="list-disc pl-5">
            {issues.map((issue) => (
              <li key={issue}>{t(`drbfm.workflow.cards.integrityReasons.${issue}`, issue)}</li>
            ))}
          </ul>
          {held && !confirmation.offered && (
            <p>
              {t(
                'drbfm.workflow.cards.integrityHint',
                '旧新の値を入力資料どおりに修正すると、理由が解消して採用できます。',
              )}
            </p>
          )}
          {confirmation.offered && onConfirmComparability && !confirmForm.open && (
            <Button
              variant="outline"
              className="mt-2"
              disabled={isLoading}
              onClick={() => confirmForm.setOpen(true)}
            >
              {t('drbfm.workflow.cards.confirmComparability', '比較可能性を確認（実際の仕様変更）')}
            </Button>
          )}
          {confirmation.offered && confirmForm.open && (
            <div className="mt-2 flex flex-col gap-2">
              <p>
                {t(
                  'drbfm.workflow.cards.confirmComparabilityHint',
                  '旧新とも入力資料どおりで、基準・単位・項目の違いが実際の仕様変更であることを確認します。抜粋は検証済みの根拠にはなりません。',
                )}
              </p>
              <Field label={t('drbfm.workflow.cards.confirmationRationale', '確認の理由（必須）')}>
                {(control) => (
                  <AutoGrowTextarea
                    {...control}
                    rows={2}
                    maxLength={2000}
                    className={CARD_TEXTAREA}
                    value={confirmForm.rationale}
                    onChange={(event) => confirmForm.setRationale(event.target.value)}
                  />
                )}
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button disabled={isLoading || !confirmForm.canSubmit} onClick={confirmForm.submit}>
                  {t('drbfm.workflow.cards.submitConfirmation', '確認を記録')}
                </Button>
                <Button variant="outline" onClick={() => confirmForm.setOpen(false)}>
                  {t('drbfm.workflow.cards.cancelConfirmation', 'キャンセル')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-4 min-[900px]:flex-row">
        <Field label={t('drbfm.workflow.cards.oldSpecification', '旧仕様')} className="min-w-0 flex-1">
          {(control) => (
            <Input
              {...control}
              className={CARD_INPUT}
              value={draft.oldSpecification}
              onChange={(event) => edit('oldSpecification', event.target.value)}
              onBlur={() => saveEdit('oldSpecification')}
            />
          )}
        </Field>
        <Field label={t('drbfm.workflow.cards.newSpecification', '新仕様')} className="min-w-0 flex-1">
          {(control) => (
            <Input
              {...control}
              className={CARD_INPUT}
              value={draft.newSpecification}
              onChange={(event) => edit('newSpecification', event.target.value)}
              onBlur={() => saveEdit('newSpecification')}
            />
          )}
        </Field>
      </div>

      <Field label={t('drbfm.workflow.cards.changeDescription', '変更内容')}>
        {(control) => (
          <AutoGrowTextarea
            {...control}
            rows={2}
            className={CARD_TEXTAREA}
            value={draft.changeDescription}
            onChange={(event) => edit('changeDescription', event.target.value)}
            onBlur={() => saveEdit('changeDescription')}
          />
        )}
      </Field>

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={isLoading || incomplete || held}
          onMouseDown={keepFocus}
          onClick={() => decide('accepted')}
        >
          {t('drbfm.workflow.cards.accept', '採用')}
        </Button>
        <Button
          variant="outline"
          disabled={isLoading}
          onMouseDown={keepFocus}
          onClick={() => decide('needs_info')}
        >
          {t('drbfm.workflow.cards.needsInfo', '要確認')}
        </Button>
        <Button
          variant="outline"
          disabled={isLoading}
          onMouseDown={keepFocus}
          onClick={() => decide('deferred')}
        >
          {t('drbfm.workflow.cards.defer', '保留')}
        </Button>
        <Button
          variant="outline"
          className={REJECT_BUTTON}
          disabled={isLoading}
          onMouseDown={keepFocus}
          onClick={() => decide('rejected')}
        >
          {t('drbfm.workflow.cards.reject', '却下')}
        </Button>
      </div>
    </div>
  );
};

export { DesignChangeCard };
